import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@machinemetrics/mm-react-components';
import { ArrowLeft, Loader2, Settings2, RotateCcw } from 'lucide-react';
import { postMesDevReset } from '../services/erpApi';

const SIM_API_BASE = '/machinesim/api';

export default function SimCommandCenter() {
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMessage, setResetMessage] = useState(null);
  const [resetError, setResetError] = useState(null);

  useEffect(() => {
    const fetchMachineTypes = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${SIM_API_BASE}/sim-defs/machine-types`);
        if (!res.ok) throw new Error('Failed to fetch machine types');
        const data = await res.json();
        setMachineTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
        setMachineTypes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMachineTypes();
  }, []);

  const capabilitiesCount = (mt) => {
    if (!mt.capabilities || typeof mt.capabilities !== 'object') return 0;
    return Object.keys(mt.capabilities).length;
  };

  const handleMesDevReset = async () => {
    setResetMessage(null);
    setResetError(null);
    const ok = window.confirm(
      'Reset MES / simulator sandbox?\n\n' +
        '• Spork work orders (WO-SPK-2026-*): completed qty → 0, status → RELEASED, labor tickets removed.\n' +
        '• All simulator machines: READY, no MES binding, part count and tool life counters cleared.\n\n' +
        'This does not delete work orders or parts.',
    );
    if (!ok) return;
    setResetBusy(true);
    try {
      const data = await postMesDevReset();
      setResetMessage(
        `Done. WOs updated: ${data.workOrdersUpdated}, ops: ${data.workOrderOperationsUpdated}, labor tickets removed: ${data.laborTicketsDeleted}, machines reset: ${data.machinesReset}. Open the shop floor or machine page if it was already open.`,
      );
    } catch (e) {
      setResetError(e.response?.data?.error || e.message || 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-muted-foreground" />
              <CardTitle>Simulation Command Center</CardTitle>
            </div>
            <CardDescription>
              Machine type definitions from the sim-defs API. No editing yet—proof that the API is consumable.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="w-8 h-8 animate-spin mr-2" />
                Loading machine types…
              </div>
            ) : error ? (
              <p className="text-destructive py-4">{error}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Code</th>
                      <th className="text-left py-2 font-medium">Name</th>
                      <th className="text-left py-2 font-medium">Family</th>
                      <th className="text-left py-2 font-medium">Capabilities</th>
                    </tr>
                  </thead>
                  <tbody>
                    {machineTypes.map((mt) => (
                      <tr key={mt.id} className="border-b last:border-0">
                        <td className="py-2 font-mono">{mt.code}</td>
                        <td className="py-2">{mt.name}</td>
                        <td className="py-2">{mt.family}</td>
                        <td className="py-2">{capabilitiesCount(mt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {machineTypes.length === 0 && !loading && !error && (
                  <p className="text-muted-foreground py-4">No machine types defined. Run the seed to populate.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6 border-amber-900/40">
          <CardHeader>
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              <CardTitle>MES / simulator dev reset</CardTitle>
            </div>
            <CardDescription>
              Puts spork ERP work orders and every simulator machine back to a clean &quot;first job&quot; state while
              keeping WO and part rows. For local development only (server returns 403 in production unless{' '}
              <span className="font-mono">ALLOW_MES_DEV_RESET=1</span>).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <button
              type="button"
              disabled={resetBusy}
              onClick={handleMesDevReset}
              className="inline-flex items-center gap-2 rounded-md bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
            >
              {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Reset sandbox
            </button>
            {resetMessage && <p className="text-sm text-emerald-600 dark:text-emerald-400">{resetMessage}</p>}
            {resetError && <p className="text-sm text-destructive">{resetError}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
